"use client";

import { useBookingResume } from "~/hooks/use-booking-resume";
import Link from "next/link";
import { Suspense, useEffect, useState } from "react";

import Logo from "~/components/logo";
import {
  PublicBookingCta,
  type PublicBookingAction,
} from "./public-booking-cta";
import { PublicAccountLink } from "~/components/patterns/public-account-link";
import { PublicMobileMenu } from "~/components/patterns/public-mobile-menu";
import {
  PUBLIC_NAVIGATION,
  trackPublicNavClick,
} from "~/components/patterns/public-navigation";
import { buttonVariants } from "~/components/ui/button";
import { Avatar, AvatarImage } from "~/components/ui/avatar";
import { capturePostHogEvent } from "~/lib/posthog/client";
import { cn } from "~/lib/utils";

export default function Header({
  trackingSurface,
  showBookingCta = true,
  bookingAction,
  transparent = false,
  darkAtTop = false,
}: {
  trackingSurface?: "homepage";
  showBookingCta?: boolean;
  bookingAction?: PublicBookingAction;
  transparent?: boolean;
  darkAtTop?: boolean;
}) {
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    if (!transparent) return;

    const syncScrollState = () => setHasScrolled(window.scrollY > 8);
    syncScrollState();
    window.addEventListener("scroll", syncScrollState, { passive: true });

    return () => window.removeEventListener("scroll", syncScrollState);
  }, [transparent]);

  // Transparency and the surface beneath it are separate concerns. Most page
  // tops are light; only genuine dark heroes opt into light navigation content.
  const isOverDark = transparent && darkAtTop && !hasScrolled;

  return (
    <header
      className={cn(
        "sticky top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-200",
        transparent && "-mb-16",
        transparent
          ? hasScrolled
            ? "border-brand-forest/10 bg-background/90 border-b backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
          : "border-border/80 bg-background/95 border-b backdrop-blur-xs",
      )}
    >
      <div className="container mx-auto grid h-16 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 px-4 md:px-8">
        <Link
          href="/"
          className="col-start-1 flex min-h-11 items-center"
          aria-label="Rešimo Probleme, početna stranica"
        >
          <Logo surface={isOverDark ? "dark" : "light"} compactOnMobile />
        </Link>

        <nav
          className={cn(
            "col-start-2 hidden items-center gap-7 text-sm font-medium lg:flex",
            isOverDark ? "text-white" : "text-foreground",
          )}
          aria-label="Glavna navigacija"
        >
          {PUBLIC_NAVIGATION.primary.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={cn(
                "focus-visible:ring-brand rounded-sm focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-hidden",
                isOverDark
                  ? "hover:text-editorial-paper-contrast"
                  : "hover:text-brand",
              )}
              onClick={() =>
                trackPublicNavClick({
                  destination: link.href,
                  location: "public_header",
                })
              }
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="col-start-3 flex items-center gap-2 justify-self-end">
          <Suspense
            fallback={
              <HeaderControls
                bookingAction={bookingAction}
                showBookingCta={showBookingCta}
                isHomepage={trackingSurface === "homepage"}
                isOverDark={isOverDark}
              />
            }
          >
            <ResumableHeaderControls
              bookingAction={bookingAction}
              showBookingCta={showBookingCta}
              isHomepage={trackingSurface === "homepage"}
              isOverDark={isOverDark}
            />
          </Suspense>
        </div>
      </div>
    </header>
  );
}

interface HeaderControlsProps {
  bookingAction?: PublicBookingAction;
  showBookingCta: boolean;
  isHomepage: boolean;
  isOverDark: boolean;
  resumeUrl?: string | null;
  cartItem?: ReturnType<typeof useBookingResume>["cartItem"];
}

function ResumableHeaderControls(props: HeaderControlsProps) {
  const { resumeUrl, cartItem } = useBookingResume();
  return (
    <HeaderControls {...props} resumeUrl={resumeUrl} cartItem={cartItem} />
  );
}

function HeaderControls({
  bookingAction,
  showBookingCta,
  isHomepage,
  isOverDark,
  resumeUrl,
  cartItem,
}: HeaderControlsProps) {
  const resumeBooking = !bookingAction && resumeUrl;
  const resolvedAction = resumeBooking
    ? { type: "navigate" as const, href: resumeBooking }
    : bookingAction;
  const trackHomepageCta = (placement: "header" | "mobile_menu") => {
    if (!isHomepage) return;

    capturePostHogEvent("homepage_cta_clicked", {
      placement,
      destination: resumeBooking ? "booking_resume" : "availability",
    });
  };

  return (
    <>
      <PublicMobileMenu
        bookingAction={resolvedAction}
        resumeBooking={!!resumeBooking}
        resumeTherapist={
          resumeBooking && cartItem
            ? {
                name: cartItem.therapistName,
                image: cartItem.therapistImage,
              }
            : undefined
        }
        showBookingCta={showBookingCta}
        onNavigate={(destination) =>
          trackPublicNavClick({
            destination,
            location: "public_mobile_menu",
          })
        }
        onBookingCtaClick={
          isHomepage ? () => trackHomepageCta("mobile_menu") : undefined
        }
        light={isOverDark}
      />
      <PublicAccountLink light={isOverDark} />
      {showBookingCta && (
        <PublicBookingCta
          action={resolvedAction}
          onClick={() => trackHomepageCta("header")}
          className={buttonVariants({
            variant: "cta",
            size: "sm",
            className: "h-11! px-4 font-semibold",
          })}
        >
          {resumeBooking && cartItem?.therapistImage && (
            <Avatar className="mr-2 h-6 w-6" aria-hidden="true">
              <AvatarImage src={cartItem.therapistImage} alt="" />
            </Avatar>
          )}
          <span className="hidden sm:inline">
            {resumeBooking ? "Nastavi zakazivanje" : "Zakaži prvi razgovor"}
          </span>
          <span className="sm:hidden">
            {resumeBooking ? "Nastavi" : "Zakaži"}
          </span>
        </PublicBookingCta>
      )}
    </>
  );
}
